'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  proOnly?: boolean;
  eliteOnly?: boolean;
  icon: React.ReactNode;
}

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px', opacity: 0.5 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const HamburgerIcon = ({ open }: { open: boolean }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    {open ? (
      <>
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </>
    ) : (
      <>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <line x1="3" y1="12" x2="21" y2="12"/>
        <line x1="3" y1="18" x2="21" y2="18"/>
      </>
    )}
  </svg>
);

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { tenant, isPro, isElite, isTrialing, trialDaysLeft, isStarter } = useTenant();
  const { user, signOut } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  const navSections = [
    {
      label: 'PRINCIPAL',
      items: [
        { label: 'Dashboard', href: '/admin/dashboard', proOnly: false, eliteOnly: false, icon: '⊞' },
        { label: 'Pipeline de ventas', href: '/admin/pipeline', proOnly: true, eliteOnly: false, icon: '⟶' },
      ],
    },
    {
      label: 'INVENTARIO',
      items: [
        { label: 'Inventario completo', href: '/admin/inventory', proOnly: false, eliteOnly: false, icon: '☰' },
        { label: 'Agregar vehículo', href: '/admin/vehicles/new', proOnly: false, eliteOnly: false, icon: '+' },
        { label: 'Vendidos (archivo)', href: '/admin/vendidos', proOnly: false, eliteOnly: false, icon: '✓' },
        { label: 'Tarjeta de marketing', href: '/admin/card', proOnly: true, eliteOnly: false, icon: '◈' },
      ],
    },
    ...(isElite ? [{
      label: 'MI SITIO WEB',
      items: [
        { label: 'Mi Sitio Web', href: '/admin/sitio-web', proOnly: false, eliteOnly: true, icon: '🌐' },
      ],
    }] : []),
    {
      label: 'RECREATIVOS',
      items: [
        { label: 'Can-Am', href: '/admin/can-am', proOnly: true, eliteOnly: false, icon: '◎' },
        { label: 'Sea-Doo', href: '/admin/seadoo', proOnly: true, eliteOnly: false, icon: '◎' },
      ],
    },
    {
      label: 'NEGOCIO',
      items: [
        { label: 'Clientes (CRM)', href: '/admin/crm', proOnly: true, eliteOnly: false, icon: '◉' },
        { label: 'Finanzas', href: '/admin/finance', proOnly: true, eliteOnly: false, icon: '$' },
        { label: 'Alertas', href: '/admin/alerts', proOnly: true, eliteOnly: false, icon: '⚠' },
      ],
    },
    {
      label: 'CONFIGURACIÓN',
      items: [
        { label: 'Equipo & Roles', href: '/admin/team', proOnly: true, eliteOnly: false, icon: '◈' },
        { label: 'Facturación', href: '/admin/billing', proOnly: false, eliteOnly: false, icon: '◇' },
        { label: 'Ajustes generales', href: '/admin/settings', proOnly: false, eliteOnly: false, icon: '⚙' },
      ],
    },
  ];

  const handleSignOut = async () => {
    await signOut();
    router.replace('/admin/login');
  };

  const showVehicleLimitBanner = isStarter && tenant && (tenant.vehicle_count >= 4);

  const planLabel = isElite ? 'Plan Elite' : isPro ? 'Plan Pro' : 'Plan Starter';
  const planColor = isElite ? '#C9A84C' : isPro ? '#C8102E' : '#888888';
  const planBg = isElite ? 'rgba(201,168,76,0.15)' : isPro ? 'rgba(200,16,46,0.15)' : 'rgba(255,255,255,0.05)';

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{ padding: '24px 20px', borderBottom: '1px solid #1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: '20px', color: '#FFFFFF', letterSpacing: '0.08em' }}>
            Drive<span style={{ color: '#C8102E' }}>X</span>
          </div>
          <div style={{ fontSize: '10px', color: '#555555', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '2px' }}>
            {tenant?.company_name || 'Panel de control'}
          </div>
        </div>
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            style={{ background: 'transparent', border: 'none', color: '#888888', cursor: 'pointer', padding: '4px' }}
          >
            <HamburgerIcon open />
          </button>
        )}
      </div>

      {/* Plan badge */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid #1A1A1A' }}>
        <span style={{
          background: planBg,
          color: planColor,
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          padding: '4px 10px',
          borderRadius: '4px',
        }}>
          {planLabel}
        </span>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '16px 0', overflowY: 'auto' }}>
        {navSections.map((section) => (
          <div key={section.label} style={{ marginBottom: '8px' }}>
            <div style={{
              padding: '8px 20px 4px',
              fontSize: '9px',
              fontWeight: 700,
              color: '#3A3A3A',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}>
              {section.label}
            </div>
            {section.items.map((item) => {
              const isLocked = item.proOnly && !isPro;
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '9px 20px',
                    fontSize: '12px',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#FFFFFF' : isLocked ? '#3A3A3A' : '#888888',
                    background: isActive ? 'rgba(200,16,46,0.12)' : 'transparent',
                    borderLeft: isActive ? '2px solid #C8102E' : '2px solid transparent',
                    textDecoration: 'none',
                    opacity: isLocked ? 0.5 : 1,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span style={{ fontSize: '14px', width: '16px', textAlign: 'center' }}>{item.icon}</span>
                  {item.label}
                  {isLocked && <LockIcon />}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid #1A1A1A' }}>
        {isElite && tenant?.slug && (
          <a href={`/${tenant.slug}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#C9A84C', textDecoration: 'none', marginBottom: '10px', padding: '6px', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px' }}>
            Ver mi sitio web →
          </a>
        )}
        {!isElite && (
          <a href="/catalogue" target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#555555', textDecoration: 'none', marginBottom: '10px', padding: '6px', border: '1px solid #2A2A2A', borderRadius: '4px' }}>
            Ver catálogo público →
          </a>
        )}
        <div style={{ fontSize: '11px', color: '#555555', marginBottom: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.email}
        </div>
        <button
          onClick={handleSignOut}
          style={{
            background: 'transparent',
            border: '1px solid #2A2A2A',
            color: '#888888',
            fontSize: '11px',
            fontWeight: 600,
            padding: '6px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            width: '100%',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F5F5F5', fontFamily: 'Montserrat, sans-serif' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside style={{
          width: '240px',
          minWidth: '240px',
          background: '#0A0A0A',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 50,
          overflowY: 'auto',
        }}>
          <SidebarContent />
        </aside>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              zIndex: 60,
            }}
          />
          {/* Drawer */}
          <aside style={{
            width: '280px',
            background: '#0A0A0A',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 70,
            overflowY: 'auto',
            boxShadow: '4px 0 24px rgba(0,0,0,0.8)',
          }}>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main content */}
      <div style={{ marginLeft: isMobile ? 0 : '240px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          background: '#FFFFFF',
          borderBottom: '1px solid #E5E5E5',
          padding: '0 16px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 40,
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#0A0A0A',
                  cursor: 'pointer',
                  padding: '4px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <HamburgerIcon />
              </button>
            )}
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {tenant?.company_name || 'DriveX Admin'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {isStarter && (
              <a href="/admin/billing" style={{
                background: '#C8102E',
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: 700,
                padding: '6px 12px',
                borderRadius: '4px',
                textDecoration: 'none',
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
              }}>
                {isMobile ? 'Upgrade' : 'Actualizar a Pro'}
              </a>
            )}
            {isPro && !isElite && (
              <a href="/admin/billing" style={{
                background: 'rgba(201,168,76,0.1)',
                color: '#C9A84C',
                fontSize: '11px',
                fontWeight: 700,
                padding: '6px 12px',
                borderRadius: '4px',
                textDecoration: 'none',
                letterSpacing: '0.05em',
                border: '1px solid rgba(201,168,76,0.3)',
                whiteSpace: 'nowrap',
              }}>
                {isMobile ? 'Elite' : 'Actualizar a Elite'}
              </a>
            )}
          </div>
        </header>

        {/* Trial banner */}
        {isPro && isTrialing && (
          <div style={{
            background: '#FEF3C7',
            borderBottom: '1px solid #F59E0B',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            fontWeight: 600,
            color: '#92400E',
            gap: '8px',
            flexWrap: 'wrap',
          }}>
            <span>⏱ Prueba gratis — te quedan {trialDaysLeft} días.</span>
            <a href="/admin/billing" style={{ color: '#C8102E', textDecoration: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Agregar método de pago →
            </a>
          </div>
        )}

        {/* Vehicle limit banner */}
        {showVehicleLimitBanner && (
          <div style={{
            background: '#C8102E',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            fontWeight: 600,
            color: '#FFFFFF',
            gap: '8px',
            flexWrap: 'wrap',
          }}>
            <span>
              Usando {tenant?.vehicle_count}/5 vehículos. Actualiza para agregar más.
            </span>
            <a href="/admin/billing" style={{ color: '#FFFFFF', textDecoration: 'none', fontWeight: 700, borderBottom: '1px solid rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
              Actualizar →
            </a>
          </div>
        )}

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? '16px 12px' : '24px' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
